import { useEffect, useRef, useState } from "react";
import type { Theme } from "./theme";

// A flat plane strip is deformed by the vertex shader onto a cubic Bezier
// spline with twist, giving a true 3D twisted ribbon. The fragment shader
// paints silk fibre striations, a drifting fold highlight, a colour gradient
// along the length, and a fresnel-style edge fade. Motion comes from slow
// time-modulation of the spline control points and twist phase.
const VERTEX_SHADER = `
uniform float u_time;
uniform vec3 u_cp0;
uniform vec3 u_cp1;
uniform vec3 u_cp2;
uniform vec3 u_cp3;

varying vec2 vUv;
varying vec3 vNormalW;
varying vec3 vViewDir;

vec3 cubicBezier(float t, vec3 p0, vec3 p1, vec3 p2, vec3 p3) {
  float u = 1.0 - t;
  return u * u * u * p0
       + 3.0 * u * u * t * p1
       + 3.0 * u * t * t * p2
       + t * t * t * p3;
}

vec3 cubicBezierTangent(float t, vec3 p0, vec3 p1, vec3 p2, vec3 p3) {
  float u = 1.0 - t;
  return 3.0 * u * u * (p1 - p0)
       + 6.0 * u * t * (p2 - p1)
       + 3.0 * t * t * (p3 - p2);
}

void main() {
  float along = uv.x;
  float across = uv.y - 0.5;

  // Slow wobble applied to the inner control points — gives the whole ribbon
  // a gentle living undulation without fully regenerating geometry.
  float wob = u_time * 0.18;
  vec3 cp1 = u_cp1 + vec3(sin(wob * 0.9) * 0.05, cos(wob * 0.7) * 0.05, sin(wob * 1.3) * 0.10);
  vec3 cp2 = u_cp2 + vec3(cos(wob * 0.8) * 0.05, sin(wob * 1.1) * 0.05, cos(wob * 0.6) * 0.10);

  vec3 p = cubicBezier(along, u_cp0, cp1, cp2, u_cp3);
  vec3 tangent = normalize(cubicBezierTangent(along, u_cp0, cp1, cp2, u_cp3));

  // Build a stable frame perpendicular to the tangent.
  vec3 up = vec3(0.0, 0.0, 1.0);
  vec3 binormal = normalize(cross(tangent, up));
  vec3 normal = normalize(cross(binormal, tangent));

  // Twist the frame around the tangent. With a real cross-section thickness
  // (added below) the ribbon no longer reads as a zero-width line when it
  // passes edge-on, so we can afford a substantial twist — ~0.9 turns over
  // the visible length plus a slow temporal oscillation.
  float twistPhase = 0.35 * sin(u_time * 0.18);
  float tw = (along - 0.5) * 5.65 + twistPhase;
  vec3 b = binormal * cos(tw) + normal * sin(tw);
  vec3 n = -binormal * sin(tw) + normal * cos(tw);

  // Ribbon width — wide overall, with a subtle mid-length bulge so it still
  // reads as a sheet rather than a uniform strip, but never goes narrow.
  float widthProfile = 1.0 + 0.25 * sin(along * 3.14159);
  float halfWidth = 0.28 * widthProfile;

  // Parabolic cross-section thickness in the normal direction: gives the
  // ribbon an actual 3D shape so that at edge-on twist angles we see a
  // curved arc rather than a razor-thin line.
  float thicknessProfile = 1.0 - 4.0 * across * across;  // 1 at centre, 0 at edges
  float thickness = 0.065;
  vec3 thicknessOffset = n * thickness * thicknessProfile;

  vec3 offset = b * halfWidth * (across * 2.0);
  vec3 finalPos = p + offset + thicknessOffset;

  // Approximate surface normal for the curved cross-section: tilted outward
  // at the edges (in b direction) and flat at the centre (in n direction).
  vec3 surfaceNormal = normalize(b * (8.0 * thickness * across) + n * (2.0 * halfWidth));

  vUv = uv;
  vNormalW = normalize(normalMatrix * surfaceNormal);
  vec4 mvPosition = modelViewMatrix * vec4(finalPos, 1.0);
  vViewDir = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const FRAGMENT_SHADER = `
precision highp float;

uniform float u_time;
uniform vec3 u_color0;
uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;
uniform vec3 u_color4;

varying vec2 vUv;
varying vec3 vNormalW;
varying vec3 vViewDir;

void main() {
  float along = vUv.x;
  float across = vUv.y;
  float acrossCentered = across - 0.5;

  // Along-ribbon colour grading — Rarion accent palette extended with
  // complementary rose, rose-violet and dusty teal so we get Stripe's
  // visual vocabulary without copying its blue/purple identity.
  vec3 color;
  if (along < 0.22) {
    color = mix(u_color4, u_color0, smoothstep(0.00, 0.22, along));
  } else if (along < 0.45) {
    color = mix(u_color0, u_color1, smoothstep(0.22, 0.45, along));
  } else if (along < 0.70) {
    color = mix(u_color1, u_color2, smoothstep(0.45, 0.70, along));
  } else {
    color = mix(u_color2, u_color3, smoothstep(0.70, 1.00, along));
  }

  // Across-ribbon shading: gentle centre brightening without darkening the
  // edges much — keeps the ribbon feeling full-width even when it twists.
  float edgeFalloff = 1.0 - pow(abs(acrossCentered) * 2.0, 2.0);
  color *= 0.88 + 0.20 * edgeFalloff;

  // Fold highlight: a bright band that drifts across the ribbon over time.
  float foldPhase = 0.5 + 0.5 * sin(along * 5.0 + u_time * 0.40);
  float fold = smoothstep(0.12, 0.0, abs(across - foldPhase));
  color += vec3(1.0) * fold * 0.10;

  // Fresnel-ish rim: the silk brightens where it turns away from the camera,
  // reading as a subtle sheen on the folds.
  float fres = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewDir))), 2.0);
  color *= 0.88 + 0.25 * fres;

  // Taper opacity at the ribbon ends and edges so there are no hard cuts.
  float alphaAlong = smoothstep(-0.02, 0.08, along) * smoothstep(1.02, 0.92, along);
  float alphaAcross = smoothstep(0.00, 0.06, across) * smoothstep(1.00, 0.94, across);

  gl_FragColor = vec4(color, alphaAlong * alphaAcross);
}
`;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function canUseWebGL() {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
  } catch {
    return false;
  }
}

// Ribbon palette — primarily blue/green with a single subtle purple hint
// at color3. Muted silk-like tones, not saturated primaries.
const RIBBON_COLORS = {
  // Along-ribbon stops, top-left → bottom-right:
  color0: "#7ac4a8", // soft sea-green
  color1: "#4fa7b5", // teal
  color2: "#4e8fbc", // ocean blue
  color3: "#6c74b8", // blue-violet — the "hint of purple"
  color4: "#7cbba4", // sage / muted green
} as const;

function staticGradientStyle(theme: Theme): React.CSSProperties {
  return {
    position: "absolute",
    inset: 0,
    background: `
      radial-gradient(ellipse 14% 20% at 58% 8%, rgba(124, 187, 164, 0.55), transparent 70%),
      radial-gradient(ellipse 22% 32% at 78% 22%, rgba(122, 196, 168, 0.75), transparent 70%),
      radial-gradient(ellipse 28% 36% at 72% 52%, rgba(79, 167, 181, 0.80), transparent 70%),
      radial-gradient(ellipse 24% 32% at 92% 58%, rgba(78, 143, 188, 0.75), transparent 70%),
      radial-gradient(ellipse 20% 26% at 88% 90%, rgba(108, 116, 184, 0.60), transparent 70%),
      ${theme.bg}
    `,
  };
}

export function HeroAnimation({ theme }: { theme: Theme }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [useFallback, setUseFallback] = useState(() => {
    if (prefersReducedMotion()) return true;
    if (!canUseWebGL()) return true;
    return false;
  });

  useEffect(() => {
    if (useFallback) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    (async () => {
      const THREE = await import("three");
      if (cancelled) return;

      const renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        premultipliedAlpha: false,
      });
      renderer.setClearColor(0x000000, 0);

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      renderer.setPixelRatio(dpr);

      const scene = new THREE.Scene();

      // Orthographic camera: keep the ribbon looking crisp and 2D-like while
      // still benefiting from 3D lighting inside the ribbon's shader.
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10, 10);
      camera.position.set(0, 0, 5);

      // A flat plane strip — the vertex shader is what bends it onto the spline
      // and adds cross-section thickness. 220 segments along × 8 across lets
      // the parabolic thickness profile form a smooth arc so the ribbon keeps
      // visible depth even at edge-on twist angles.
      const geometry = new THREE.PlaneGeometry(1, 1, 220, 8);

      const uniforms = {
        u_time: { value: 0 },
        u_cp0: { value: new THREE.Vector3() },
        u_cp1: { value: new THREE.Vector3() },
        u_cp2: { value: new THREE.Vector3() },
        u_cp3: { value: new THREE.Vector3() },
        u_color0: { value: new THREE.Color(RIBBON_COLORS.color0) },
        u_color1: { value: new THREE.Color(RIBBON_COLORS.color1) },
        u_color2: { value: new THREE.Color(RIBBON_COLORS.color2) },
        u_color3: { value: new THREE.Color(RIBBON_COLORS.color3) },
        u_color4: { value: new THREE.Color(RIBBON_COLORS.color4) },
      };

      const material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: VERTEX_SHADER,
        fragmentShader: FRAGMENT_SHADER,
        transparent: true,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // Re-compute sizing and control points on resize so the ribbon stays
      // anchored relative to the viewport's aspect ratio.
      const layout = () => {
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        if (w === 0 || h === 0) return;

        renderer.setSize(w, h, false);
        const aspect = w / h;
        camera.left = -aspect;
        camera.right = aspect;
        camera.top = 1;
        camera.bottom = -1;
        camera.updateProjectionMatrix();

        // Control points, positioned on the right half of the viewport.
        // Entry just above the top edge; exit pushed past the right edge with
        // y kept shallower than the viewport bottom so the visible spline
        // (t up to ≈0.85, where x crosses aspect) stays clear of the bottom
        // edge — exiting through the right side only, not the corner.
        // The inner points push the ribbon through a soft S-curve with depth
        // variation (z) so the twist shows multiple faces.
        uniforms.u_cp0.value.set(0.35 * aspect, 1.25, 0.0);
        uniforms.u_cp1.value.set(-0.05 * aspect, 0.55, 0.35);
        uniforms.u_cp2.value.set(0.5 * aspect, -0.35, -0.35);
        uniforms.u_cp3.value.set(1.35 * aspect, -1.2, 0.0);
      };
      layout();
      window.addEventListener("resize", layout);

      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const onMqChange = (e: MediaQueryListEvent) => {
        if (e.matches) setUseFallback(true);
      };
      mq.addEventListener?.("change", onMqChange);

      const start = performance.now();
      let rafId = 0;
      const frame = () => {
        uniforms.u_time.value = (performance.now() - start) / 1000;
        renderer.render(scene, camera);
        rafId = window.requestAnimationFrame(frame);
      };
      frame();

      cleanup = () => {
        window.cancelAnimationFrame(rafId);
        window.removeEventListener("resize", layout);
        mq.removeEventListener?.("change", onMqChange);
        geometry.dispose();
        material.dispose();
        renderer.dispose();
      };
    })().catch(() => {
      if (!cancelled) setUseFallback(true);
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [useFallback]);

  if (useFallback) {
    return <div data-testid="hero-animation-fallback" style={staticGradientStyle(theme)} />;
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-animation-canvas"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
    />
  );
}
